import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import { getMyProfile } from "../../../../core/data/redux/employeesSlice";
import { toast } from "react-toastify";
import { submitStaffQuarterVacation, getMyAllotments, getAllVacationIntimations, getVacationIntimationData, getVacationRequests, downloadVacationLetter } from "../../../../core/data/redux/staffQuarterSlice";
import { encodeRouteParam } from "../../../../core/hooks/routeEncoder";

import VacationIntimationForm from "./VacationIntimationForm";
import { formatFlatNoWithBlock, resolveFlatCategory } from "../staffQuarterUtils";

const VacationIntimation: React.FC = () => {
  const routes = all_routes as any;
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();

  const employeesState = useSelector((s: any) => s.employeesState);
  const staffQuarterState = useSelector((state: any) => state.staffQuarterState);
  const user = employeesState?.data?.data;
  const vacationRequestsRaw = staffQuarterState?.vacationRequests;
  const extractVacationRequests = (raw: any): any[] => {
    if (!raw) return [];
    const candidates = [raw, raw?.data, raw?.data?.data, raw?.items, raw?.data?.items, raw?.result, raw?.data?.result];
    for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate;
    }
    return [];
  };
  const vacationRequests = extractVacationRequests(vacationRequestsRaw);
  const allotments = staffQuarterState?.myAllotments?.data || [];

  const initialTab = (location.state as any)?.activeTab || "INTIMATION_FORM";
  const [activeMainTab, setActiveMainTab] = useState(initialTab);

  const [searchQuery, setSearchQuery] = useState("");
  const [isFormVisible, setIsFormVisible] = useState((location.state as any)?.showForm || false);
  const [selectedAllotment, setSelectedAllotment] = useState<any>(null);

  const [form, setForm] = useState({
    tentativeVacationDate: "",
    reasonForVacation: "",
    hraClaim: "No",
    remarks: "",
  });

  const extractIntimationsArray = (state: any): any[] => {
    const sources = [
      state?.vacationIntimationData,
      state?.allVacations
    ];

    for (const raw of sources) {
      if (!raw) continue;
      if (Array.isArray(raw)) return raw;
      if (Array.isArray(raw?.data)) return raw.data;
      if (Array.isArray(raw?.result)) return raw.result;
      if (Array.isArray(raw?.items)) return raw.items;
      if (raw?.data?.data && Array.isArray(raw.data.data)) return raw.data.data;
      if (typeof raw === "object" && (raw.id || raw.vacationDate || raw.vacationIntimationDate)) return [raw];
    }
    return [];
  };

  const allIntimations = extractIntimationsArray(staffQuarterState);

  const matchedIntimation = selectedAllotment
    ? allIntimations.find((item: any) =>
      String(item.allotmentId || item.id) === String(selectedAllotment.id || selectedAllotment.allotmentId) ||
      String(item.registrationId) === String(selectedAllotment.registrationId || selectedAllotment.id) ||
      String(item.flatNumber || item.flatNo) === String(selectedAllotment.flatNumber || selectedAllotment.flatNo) ||
      String(item.allotmentReferenceNumber || item.referenceNumber) === String(selectedAllotment.referenceNumber || selectedAllotment.allotmentReferenceNumber)
    ) || (allIntimations.length > 0 ? allIntimations[0] : null)
    : (allIntimations.length > 0 ? allIntimations[0] : null);

  const rawIntimatedDate = matchedIntimation?.vacationDate || matchedIntimation?.vacationIntimationDate || matchedIntimation?.tentativeVacationDate;

  useEffect(() => {
    dispatch(getMyProfile({}));
    dispatch(getMyAllotments({}));
    dispatch(getAllVacationIntimations({}));
    dispatch(getVacationIntimationData({}));
  }, [dispatch]);

  useEffect(() => {
    if (selectedAllotment) {
      dispatch(getAllVacationIntimations({
        allotmentReferenceNumber: selectedAllotment.referenceNumber || selectedAllotment.allotmentReferenceNumber
      }));
      dispatch(getVacationRequests({
        allotmentReferenceNumber: selectedAllotment.referenceNumber || selectedAllotment.allotmentReferenceNumber
      }));
    }
  }, [dispatch, selectedAllotment]);

  useEffect(() => {
    if (allotments.length > 0 && !selectedAllotment) {
      setSelectedAllotment(allotments[0]);
    }
  }, [allotments, selectedAllotment]);

  useEffect(() => {
    if (location.state) {
      const state = location.state as any;
      if (state.showForm !== undefined) setIsFormVisible(state.showForm);
      if (state.activeTab !== undefined) setActiveMainTab(state.activeTab);
    }
  }, [location.state]);

  const filteredVacations = vacationRequests.filter((req: any) => {
    const searchStr = (req.registrationReferenceNumber || req.registrationId || "").toLowerCase();
    const matchesSearch = searchStr.includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target as any;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.tentativeVacationDate || !form.reasonForVacation || !selectedAllotment) {
      toast.error("Please fill mandatory fields and select an allotment");
      return;
    }

    const occDateStr = selectedAllotment.occupiedDate || selectedAllotment.occupationDate || selectedAllotment.occupationdate;
    if (occDateStr) {
      const occDate = new Date(occDateStr);
      occDate.setHours(0, 0, 0, 0);
      const vacDate = new Date(form.tentativeVacationDate);
      vacDate.setHours(0, 0, 0, 0);

      if (vacDate <= occDate) {
        toast.error("Vacation Date must be at least 1 day after Occupation Date");
        return;
      }
    }

    const payload = {
      registrationReferenceNumber: String(selectedAllotment.registrationReferenceNumber || selectedAllotment.registrationId || selectedAllotment.registrationRef || "SQR-2026-001"),
      allotmentReferenceNumber: String(selectedAllotment.referenceNumber || selectedAllotment.allotmentReferenceNumber || selectedAllotment.allotmentRef || "SQA-2026-001"),
      flatNumber: String(selectedAllotment.flatNumber || selectedAllotment.flatNo || ""),
      flatId: String(selectedAllotment.flatId || selectedAllotment.flat?.flatId || selectedAllotment.flatDetails?.flatId || selectedAllotment.id || ""),
      quarterType: String(selectedAllotment.quarterType || ""),
      location: String(selectedAllotment.location || selectedAllotment.locationName || ""),
      pool: String(selectedAllotment.pool || selectedAllotment.poolName || "General"),
      reasonForVacation: form.reasonForVacation,
      tentativeVacationDate: form.tentativeVacationDate,
      remarks: form.remarks,
    };

    dispatch(submitStaffQuarterVacation(payload)).then((res: any) => {
      if (res.payload?.success || res.payload?.status === 200) {
        toast.success(res.payload?.message || "Vacation request submitted successfully");
        setIsFormVisible(false);
        dispatch(getVacationRequests({
          allotmentReferenceNumber: selectedAllotment.referenceNumber || selectedAllotment.allotmentReferenceNumber
        }));
      } else {
        toast.error(res.payload?.message || "Failed to submit vacation request");
      }
    });
  };

  const handleDownloadVacationLetter = (req: any) => {
    const vacId = req.vacationId || req.id_vacation || req.vacation_id || req.id || 62;
    const regId = req.registrationId || req.id_registration || req.registration_id || req.regId || 2613;

    toast.info("Downloading Vacation Letter...");
    dispatch(downloadVacationLetter({ vacationId: vacId, registrationId: regId }))
      .then((res: any) => {
        if (res.payload) {
          const blob = new Blob([res.payload], { type: "application/pdf" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `Vacation_Letter_${vacId}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          toast.success("Vacation Letter downloaded successfully!");
        } else {
          toast.error("Failed to download Vacation Letter PDF.");
        }
      })
      .catch(() => {
        toast.error("Error downloading Vacation Letter PDF.");
      });
  };

  const formatDate = (date: any) => {
    if (!date) return "-";
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1 text-dark fw-bold">Vacation Request</h3>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to={routes.employeeDashboard || "#"}>Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to={routes.staffQuarter?.overview || "#"}>Staff Quarters</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Vacation Request</li>
              </ol>
            </nav>
          </div>
          {activeMainTab === "REQUEST_VACATION" && (
            <div className="d-flex align-items-center gap-2">
              {isFormVisible ? (
                <button className="btn btn-outline-secondary btn-sm px-3 fw-bold" onClick={() => setIsFormVisible(false)}>
                  <i className="ti ti-arrow-left me-1"></i>
                  Back to List
                </button>
              ) : (
                <button className="btn btn-danger btn-sm px-3 fw-bold" onClick={() => setIsFormVisible(true)}>
                  <i className="ti ti-plus me-1"></i>
                  New Vacation Request
                </button>
              )}
            </div>
          )}
        </div>

        {/* Minimalist Standardized Tab Navigation */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '12px', backgroundColor: '#ffffff' }}>
          <div className="card-body p-0 border-bottom">
            <ul className="nav nav-tabs border-0 px-3 mb-0" style={{ gap: '24px' }} role="tablist">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link border-0 fw-bold py-3 px-1 d-flex align-items-center gap-2 ${activeMainTab === "INTIMATION_FORM"
                    ? "active text-danger border-bottom border-danger"
                    : "text-muted"
                    }`}
                  onClick={() => setActiveMainTab("INTIMATION_FORM")}
                  style={{
                    backgroundColor: "transparent",
                    cursor: "pointer",
                    fontSize: "14px",
                    borderBottom: activeMainTab === "INTIMATION_FORM" ? "3px solid #dc3545" : "3px solid transparent",
                    transition: "all 0.2s ease"
                  }}
                >
                  <i className="ti ti-file-text" style={{ fontSize: '18px' }}></i>
                  <span>Vacation Intimation Form</span>
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link border-0 fw-bold py-3 px-1 d-flex align-items-center gap-2 ${activeMainTab === "REQUEST_VACATION"
                    ? "active text-danger border-bottom border-danger"
                    : "text-muted"
                    }`}
                  onClick={() => {
                    setActiveMainTab("REQUEST_VACATION");
                    setIsFormVisible(false);
                  }}
                  style={{
                    backgroundColor: "transparent",
                    cursor: "pointer",
                    fontSize: "14px",
                    borderBottom: activeMainTab === "REQUEST_VACATION" ? "3px solid #dc3545" : "3px solid transparent",
                    transition: "all 0.2s ease"
                  }}
                >
                  <i className="ti ti-calendar-minus" style={{ fontSize: '18px' }}></i>
                  <span>Request Vacation</span>
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Tab Content */}
        {activeMainTab === "INTIMATION_FORM" ? (
          VacationIntimationForm ? (
            <VacationIntimationForm embedded={true} onSuccess={() => dispatch(getAllVacationIntimations({}))} />
          ) : (
            <div className="alert alert-info">Loading Vacation Intimation Form...</div>
          )
        ) : (
          <>
            {!isFormVisible && (
              <div className="card shadow-sm border-0" style={{ borderRadius: '12px' }}>
                <div className="card-header bg-white py-3 border-bottom d-flex flex-wrap justify-content-between align-items-center gap-3" style={{ borderRadius: '12px 12px 0 0' }}>
                  <div className="d-flex align-items-center gap-2">
                    <h5 className="card-title mb-0 fw-bold text-dark">Vacation Requests History</h5>
                    <span className="badge bg-light text-dark border ms-2">{filteredVacations.length} Records</span>
                  </div>
                  <div className="input-group input-group-sm" style={{ maxWidth: '280px' }}>
                    <span className="input-group-text bg-white border-end-0">
                      <i className="ti ti-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search Reference..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive" style={{ minHeight: '200px' }}>
                    <table className="table table-hover table-center mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>Reference</th>
                          <th>Vacation Date</th>
                          <th>Reason</th>
                          <th className="text-center">Status</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {staffQuarterState.loading === "pending" ? (
                          <tr>
                            <td colSpan={5} className="text-center py-5">
                              <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                              Loading requests...
                            </td>
                          </tr>
                        ) : filteredVacations.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-center py-4 text-muted">No vacation requests found</td>
                          </tr>
                        ) : (
                          filteredVacations.map((req: any, idx: number) => {
                            const rawStatus = req.status || req.approvalStatus || req.currentStatus || req.vacationStatus || req.requestStatus || (req.isApproved ? "APPROVED" : "PENDING");
                            const statusStr = String(rawStatus).toUpperCase();
                            const isApprovedStatus = ["APPROVED", "CONFIRMED", "COMPLETED", "ACCEPTED"].includes(statusStr);
                            const isRejectedStatus = ["REJECTED", "DECLINED", "CANCELLED"].includes(statusStr);

                            return (
                              <tr key={idx}>
                                <td className="fw-bold text-dark">{req.registrationReferenceNumber || req.registrationId || `VR-${idx + 1}`}</td>
                                <td>{formatDate(req.tentativeVacationDate)}</td>
                                <td title={req.reasonForVacation}>{req.reasonForVacation?.length > 40 ? req.reasonForVacation.substring(0, 40) + "..." : req.reasonForVacation}</td>
                                <td className="text-center">
                                  <span className={`badge ${isApprovedStatus ? 'bg-success' : isRejectedStatus ? 'bg-danger' : statusStr === 'INTIMATED' ? 'bg-info text-white' : 'bg-warning text-dark'}`}>
                                    {statusStr}
                                  </span>
                                </td>
                                <td className="text-end">
                                  <div className="d-flex align-items-center justify-content-end gap-1">
                                    <button
                                      className="btn btn-sm btn-icon btn-light border"
                                      title="View Details"
                                      onClick={() => {
                                        const requestId = req.vacationId || req.id || req.allotmentReferenceNumber || req.registrationReferenceNumber || req.registrationId;
                                        const reference = req.allotmentReferenceNumber || req.referenceNumber || "";
                                        const session = JSON.parse(localStorage.getItem("user") || "{}");
                                        const empId = String(req.empId || req.employeeNumber || user?.empId || user?.employeeNumber || user?.details?.emp_no || session.empId || session.employeeNumber || "").trim();
                                        if (!empId) {
                                          toast.error("Employee ID is unavailable. Please reload your profile.");
                                          return;
                                        }
                                        navigate(`${routes.staffQuarter.vacationDetails.replace(':id', encodeRouteParam(requestId))}?type=request&empId=${encodeURIComponent(empId.padStart(8, "0"))}&allotmentReferenceNumber=${encodeURIComponent(reference)}`);
                                      }}
                                    >
                                      <i className="ti ti-eye text-primary"></i>
                                    </button>
                                    <button
                                      className={`btn btn-sm ${isApprovedStatus ? 'btn-outline-danger' : 'btn-outline-secondary opacity-50'} d-inline-flex align-items-center gap-1`}
                                      title={isApprovedStatus ? "Download Vacation Letter" : "Vacation letter is available only after approval"}
                                      disabled={!isApprovedStatus}
                                      onClick={() => isApprovedStatus && handleDownloadVacationLetter(req)}
                                    >
                                      <i className="ti ti-file-download"></i>
                                      <span className="d-none d-md-inline" style={{ fontSize: '11px' }}>Letter</span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {isFormVisible && (
              <form onSubmit={handleSubmit}>
                <div className="card mb-3">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <span>Allotted Flat Details</span>
                    {allotments.length > 1 && (
                      <select
                        className="form-select form-select-sm w-auto"
                        onChange={(e) => setSelectedAllotment(allotments.find((a: any) => a.registrationId === e.target.value))}
                        value={selectedAllotment?.registrationId}
                      >
                        {allotments.map((a: any) => (
                          <option key={a.registrationId} value={a.registrationId}>
                            {formatFlatNoWithBlock(a) || "Flat"} - {a.location || a.locationName || a.flatDetails?.location || "-"}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-3">
                        <span className="text-muted small d-block mb-1" style={{ fontSize: '12.5px', fontWeight: 500 }}>Flat Number</span>
                        <div className="fw-bold text-primary" style={{ fontSize: '15px' }}>{formatFlatNoWithBlock(selectedAllotment) || "Loading..."}</div>
                      </div>
                      <div className="col-md-2">
                        <span className="text-muted small d-block mb-1" style={{ fontSize: '12.5px', fontWeight: 500 }}>Quarter Type</span>
                        <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{selectedAllotment?.quarterType || "N/A"}</div>
                      </div>
                      <div className="col-md-2">
                        <span className="text-muted small d-block mb-1" style={{ fontSize: '12.5px', fontWeight: 500 }}>Category</span>
                        <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{resolveFlatCategory(selectedAllotment, matchedIntimation, staffQuarterState?.vacationIntimationData)}</div>
                      </div>
                      <div className="col-md-2">
                        <span className="text-muted small d-block mb-1" style={{ fontSize: '12.5px', fontWeight: 500 }}>Location</span>
                        <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{selectedAllotment?.location || selectedAllotment?.locationName || "N/A"}</div>
                      </div>
                      <div className="col-md-3">
                        <span className="text-muted small d-block mb-1" style={{ fontSize: '12.5px', fontWeight: 500 }}>Intimated Vacation Date</span>
                        {matchedIntimation ? (
                          <div className="fw-bold text-success d-flex align-items-center gap-1" style={{ fontSize: '14.5px' }}>
                            <i className="ti ti-circle-check text-success"></i>
                            {formatDate(rawIntimatedDate)}
                          </div>
                        ) : (
                          <div className="fw-bold text-danger d-flex align-items-center gap-1" style={{ fontSize: '13.5px' }}>
                            <i className="ti ti-alert-triangle-filled text-danger"></i>
                            Vacation not intimated
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card mb-4 shadow-sm border-0" style={{ borderRadius: '12px' }}>
                  <div className="card-header bg-white py-3" style={{ borderRadius: '12px 12px 0 0' }}>
                    <h5 className="card-title mb-0 fw-bold">Vacation Request Details</h5>
                  </div>
                  <div className="card-body">
                    {!matchedIntimation && (
                      <div className="alert alert-warning border-0 shadow-sm rounded-3 mb-3 d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center justify-content-center bg-warning text-white rounded-circle" style={{ width: "36px", height: "36px", flexShrink: 0 }}>
                          <i className="ti ti-alert-triangle fs-5"></i>
                        </div>
                        <div>
                          <h6 className="fw-bold mb-0 text-dark">Vacation not intimated</h6>
                          <span className="small text-muted">No formal vacation intimation record has been found for this quarter. Please submit a Vacation Intimation Form first.</span>
                        </div>
                      </div>
                    )}
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold text-dark">Intimated Date of Vacation</label>
                        {matchedIntimation ? (
                          <div className="input-group">
                            <span className="input-group-text bg-light text-success border-end-0">
                              <i className="ti ti-calendar-check"></i>
                            </span>
                            <input
                              type="text"
                              className="form-control bg-light fw-bold text-dark border-start-0"
                              value={formatDate(rawIntimatedDate)}
                              readOnly
                            />
                          </div>
                        ) : (
                          <div className="p-2 rounded border border-warning-subtle bg-warning-subtle text-warning-emphasis d-flex align-items-center gap-2">
                            <i className="ti ti-alert-triangle-filled text-warning fs-5"></i>
                            <span className="fw-bold" style={{ fontSize: '13.5px' }}>Vacation not intimated</span>
                          </div>
                        )}
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Vacation Date <span className="text-danger">*</span></label>
                        <input
                          type="date"
                          name="tentativeVacationDate"
                          value={form.tentativeVacationDate}
                          onChange={handleChange}
                          className="form-control"
                          min={(() => {
                            const dStr = selectedAllotment?.occupiedDate || selectedAllotment?.occupationDate || selectedAllotment?.occupationdate;
                            if (!dStr) return undefined;
                            const minD = new Date(dStr);
                            minD.setDate(minD.getDate() + 1);
                            return minD.toISOString().split('T')[0];
                          })()}
                          required
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">Reason for Vacation <span className="text-danger">*</span></label>
                        <textarea name="reasonForVacation" value={form.reasonForVacation} onChange={handleChange} className="form-control" rows={3} placeholder="Provide detailed reason for vacation..." required></textarea>
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">Remarks</label>
                        <textarea name="remarks" value={form.remarks} onChange={handleChange} className="form-control" rows={2} placeholder="Any additional comments..."></textarea>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="alert alert-warning border-0 shadow-sm rounded-3">
                  <strong>Note:</strong> Handover the quarter in good condition as per DMRC policy.
                </div>

                <div className="d-flex justify-content-end gap-2 mb-4">
                  <button type="button" className="btn btn-outline-secondary px-4 fw-bold" onClick={() => setIsFormVisible(false)}>Cancel</button>
                  <button type="submit" className="btn btn-danger px-4 fw-bold">Submit Vacation Request</button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VacationIntimation;
